import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { Tag } from "antd";
import "./style.css";
import MapaRota from "../../components/MapaRota";
import rastreioPublicoApi from "../../service/modules/rastreio-publico.api";
import useRecargaPeriodica from "../../util/freight/useRecargaPeriodica";
import { translatorForTheStatusOfStoppingPoints } from "../frete-copy/action/action";
import logoFortio from "../../image/logo-fortio-horizontal.png";

/** O formato do token que o backend sorteia. Fora dele nem vale a ida ao servidor. */
const FORMATO_DO_TOKEN = /^[0-9a-f]{32}$/;

/** A mesma cadência do detalhe: status, pontos e o caminhão se renovam a cada minuto. */
const RECARGA_DO_RESUMO_MS = 60 * 1000;

/** As 10 mil capturas mais novas, de 500 em 500. Uma viagem de 15 dias cabe inteira. */
const PAGINAS_DO_MAPA = 20;

/**
 * O mapa lê pelas rotas do link, sem login, e sem escuta do Firestore. O servidor decide tamanho e
 * ordem da página: daqui só sai o cursor.
 */
const FONTES_PUBLICAS = {
  buscarTrajeto: (token, { cursor } = {}) =>
    rastreioPublicoApi.buscarTrajeto(token, cursor ? { cursor } : undefined),
  novidadesDoTrajeto: (token, params) =>
    rastreioPublicoApi.novidadesDoTrajeto(token, params),
};

const LINK_INVALIDO =
  "Este link de acompanhamento não existe ou foi trocado por um novo. Peça o link atualizado a quem enviou.";

const MENSAGEM_DA_RECUSA = {
  LINK_EXPIRADO:
    "Este link de acompanhamento expirou. Peça um novo a quem enviou.",
  LINK_DESATIVADO: "Este link de acompanhamento foi desativado.",
};

function dataHora(ms) {
  return ms
    ? new Date(ms).toLocaleString("pt-BR", {
        dateStyle: "short",
        timeStyle: "short",
      })
    : "—";
}

function local(ponta) {
  return [ponta?.cidade, ponta?.uf].filter(Boolean).join(" - ") || "—";
}

function situacaoDoPonto(codigo) {
  const traduzido = translatorForTheStatusOfStoppingPoints(
    String(codigo || "").toUpperCase(),
  );
  return Array.isArray(traduzido) ? "Sem atualização" : traduzido;
}

/**
 * O que o cliente do embarcador abre pelo link, sem conta no painel. Tudo vem do backend, que confere
 * o link a cada consulta: expirado ou desativado com a página aberta, o mapa sai e as consultas param.
 */
function PaginaDeRastreioPublico() {
  const { token } = useParams();
  const tokenValido = FORMATO_DO_TOKEN.test(String(token || ""));

  const [resumo, setResumo] = useState(null);
  const [carregando, setCarregando] = useState(tokenValido);
  const [recusa, setRecusa] = useState(tokenValido ? null : LINK_INVALIDO);
  const [semConexao, setSemConexao] = useState(false);

  const carregar = useCallback(async () => {
    if (!tokenValido) return;
    try {
      const resposta = await rastreioPublicoApi.buscarResumo(token);
      setResumo(resposta?.data?.data || null);
      setRecusa(null);
      setSemConexao(false);
    } catch (e) {
      const status = e?.response?.status;
      if (status === 400 || status === 404 || status === 410) {
        setResumo(null);
        setRecusa(MENSAGEM_DA_RECUSA[e?.response?.data?.code] || LINK_INVALIDO);
      } else {
        // Queda de rede ou servidor fora: mantém o que já está na tela e tenta de novo no tique.
        setSemConexao(true);
        console.error("Erro ao carregar o acompanhamento:", status);
      }
    } finally {
      setCarregando(false);
    }
  }, [token, tokenValido]);

  useEffect(() => {
    carregar();
  }, [carregar]);

  useRecargaPeriodica(carregar, recusa ? 0 : RECARGA_DO_RESUMO_MS);

  // O mapa redesenha pelos números, não pelo objeto: recarregar o resumo não refaz a viagem.
  const freteDoMapa = useMemo(() => {
    if (!resumo) return null;
    const ultima = resumo.ultimaPosicao;
    return {
      id: token,
      firstDelivery: {
        coordinates: {
          latitude: resumo.origem?.latitude,
          longitude: resumo.origem?.longitude,
        },
      },
      lastDelivery: {
        coordinates: {
          latitude: resumo.destino?.latitude,
          longitude: resumo.destino?.longitude,
        },
      },
      lastPosition: ultima
        ? {
            latitude: ultima.latitude,
            longitude: ultima.longitude,
            timestamp: ultima.ms,
          }
        : null,
    };
  }, [token, resumo]);

  return (
    <div className="rastreio">
      <header className="rastreio__barra">
        <img src={logoFortio} alt="Fortio" className="rastreio__logo" />
        <span>Acompanhamento de frete</span>
      </header>

      <main className="rastreio__conteudo">
        {carregando ? (
          <p className="rastreio__nota">Carregando o acompanhamento...</p>
        ) : recusa ? (
          <p className="rastreio__recusa">{recusa}</p>
        ) : !resumo ? (
          <p className="rastreio__nota">
            Não foi possível carregar agora. Tentando de novo em instantes.
          </p>
        ) : (
          <>
            <section className="rastreio__cabecalho">
              <h1>
                Frete {resumo.numero ?? ""}
                {resumo.status?.descricao && (
                  <Tag color="blue">{resumo.status.descricao}</Tag>
                )}
              </h1>
              <p className="rastreio__rota">
                {local(resumo.origem)} → {local(resumo.destino)}
              </p>
              <p className="rastreio__nota">
                {resumo.ultimaPosicao?.ms
                  ? `Última posição: ${dataHora(resumo.ultimaPosicao.ms)}`
                  : "Ainda sem posição registrada."}
              </p>
              {semConexao && (
                <p className="rastreio__aviso">
                  Sem conexão com o servidor agora. Mostrando a última
                  informação recebida.
                </p>
              )}
            </section>

            <section className="rastreio__bloco">
              <MapaRota
                frete={freteDoMapa}
                fontes={FONTES_PUBLICAS}
                maxPaginas={PAGINAS_DO_MAPA}
              />
            </section>

            <section className="rastreio__bloco">
              <h2>Pontos de parada</h2>
              {resumo.pontosDeParada?.length ? (
                <ol className="rastreio__pontos">
                  {resumo.pontosDeParada.map((ponto, indice) => (
                    <li key={`${ponto.ordem ?? "sem-ordem"}-${indice}`}>
                      <strong>
                        {ponto.ordem ?? indice + 1}.{" "}
                        {ponto.tipo === "entrega" ? "Entrega" : "Coleta"}
                      </strong>
                      <span>{local(ponto)}</span>
                      <span className="rastreio__situacao">
                        {situacaoDoPonto(ponto.situacao)}
                      </span>
                    </li>
                  ))}
                </ol>
              ) : (
                <p className="rastreio__nota">
                  Este frete não tem pontos de parada.
                </p>
              )}
            </section>

            <p className="rastreio__rodape">
              Link válido até {dataHora(resumo.expiraEm)}
            </p>
          </>
        )}
      </main>
    </div>
  );
}

export default PaginaDeRastreioPublico;
